import BrandExtensionNotice from "../../../components/BrandExtensionNotice";
import NestedAccordion from "../../../components/NestedAccordion";
import FileUploadField from "@/components/ui/FileUploadField";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  FileDetails,
  RecordingStageAdminRequiredActions,
  TVideoSetup,
} from "@/types/projectType";

import { FileCard } from "../../../components/FileCard";
import { RevisionFileCard } from "../../../components/RevisionFileCard";
import { getTimeDifference } from "@/utils/getTimeDifference";
import useOrderVideoParams from "@/hooks/useOrderVideoParams";
import {
  useCreateSetupMutation,
  useUploadNewSetupMutation,
} from "@/redux/feature/orderManagement/VideoSetup/videoSetupApi";
import { useGetDriveLinkQuery } from "@/redux/feature/driveLink/driveLinkApi";
import {
  DriveFolderLinkButton,
  DriveFolderMobileHint,
} from "@/components/ui/DriveFolderLinkButton";
import { useGlobalContext } from "@/context/GlobalContext";

const VideoSetup = ({
  videoSetupData,
  projectId,
}: {
  projectId: string;
  videoSetupData: TVideoSetup;
  ActionsRequired?: RecordingStageAdminRequiredActions[];
  isRawFootageExists?: boolean;
}) => {
  const { orderId, videoId } = useOrderVideoParams();
  const timeLine = getTimeDifference(videoSetupData?.adminTimeLine);

  const [clearFiles, setClearFiles] = useState(false);
  const [setupFiles, setSetupFiles] = useState<FileDetails[]>([]);
  const [s3Uploading, setS3Uploading] = useState(false);

  const { userData } = useGlobalContext();

  const { data, isFetching: driveLinkLoading } = useGetDriveLinkQuery(
    {
      email: userData?.email as string,
      projectId,
      videoId: videoId as string,
      type: "video_setup",
    },
    { skip: !userData?.email || !projectId || !videoId },
  );

  const driveLink = data?.data?.drive_link;

  const [createSetup, { isLoading: UploadingSetupLoading }] =
    useCreateSetupMutation();
  const [uploadNewSetup, { isLoading: RevisedUploadingSetupLoading }] =
    useUploadNewSetupMutation();

  const hasDirectFile = setupFiles.length > 0;
  const directUploadIncomplete = useMemo(
    () => hasDirectFile && setupFiles.some((f) => !f.live_url),
    [hasDirectFile, setupFiles],
  );
  const submitUsesDrive = !hasDirectFile && !s3Uploading;
  const driveNotReady = submitUsesDrive && (!driveLink || driveLinkLoading);

  const isMutationLoading =
    UploadingSetupLoading || RevisedUploadingSetupLoading;

  const handleUploadSetup = useCallback(async () => {
    if (!orderId || !videoId) {
      toast.error("Missing order/video context");
      return;
    }
    if (s3Uploading) {
      toast.error(
        "Finishing upload to our servers—submit will enable when the file is ready.",
      );
      return;
    }
    if (directUploadIncomplete) {
      toast.error(
        "Finishing upload to our servers—wait until the file shows as ready, then submit.",
      );
      return;
    }
    const useDrive = setupFiles.length === 0;
    if (useDrive) {
      if (driveLinkLoading) {
        toast.error("Preparing your Drive folder—please wait a moment.");
        return;
      }
      if (!driveLink) {
        toast.error(
          "Drive folder is not ready yet. Refresh the page or try again in a moment.",
        );
        return;
      }
    }

    if (videoSetupData?.requiredActions?.requiresRevision) {
      try {
        const res = await uploadNewSetup({
          orderId,
          videoId,
          data: {
            newSetupFile: useDrive ? [] : setupFiles,
            description: "Revised Setup From Creator",
            isUploadingWithDriveLink: useDrive,
            driveLink,
          },
        }).unwrap();

        if (res?.success) {
          toast.success("Setup uploaded successfully!");
          setSetupFiles([]);
          setClearFiles(true);
        } else {
          toast.error(res.message || "Failed to upload setup");
        }
      } catch (err: unknown) {
        const e = err as { data?: { message?: string } };
        toast.error(e?.data?.message || "Failed to upload setup");
      }
      return;
    }

    const payload = {
      order_id: orderId,
      video_id: videoId,
      videoSetupVersions: [
        {
          versionNumber: 1,
          uploadedBy: "user" as const,
          description: "First version of video setup",
          file: useDrive ? [] : setupFiles,
          uploadedAt: new Date(),
          isUploadingWithDriveLink: useDrive,
          link: useDrive ? (driveLink ?? "") : "",
        },
      ],
    };

    try {
      const res = await createSetup(payload).unwrap();
      if (res?.success) {
        toast.success("Setup uploaded successfully!");
        setSetupFiles([]);
        setClearFiles(true);
      } else {
        toast.error(res.message || "Failed to upload setup");
      }
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      toast.error(e?.data?.message || "Failed to upload setup");
    }
  }, [
    orderId,
    videoId,
    s3Uploading,
    directUploadIncomplete,
    setupFiles,
    driveLinkLoading,
    driveLink,
    videoSetupData?.requiredActions?.requiresRevision,
    uploadNewSetup,
    createSetup,
  ]);

  const currentVideoSetup = videoSetupData?.videoSetupVersions?.slice(-1)[0];
  const { inProgress = false, isApproved = false } =
    videoSetupData?.status || {};
  const { requiresRevision = false, requiresApproval = false } =
    videoSetupData?.requiredActions || {};
  // Soft UI only — creators may upload setup, but it is never required.
  const isActionRequired = Boolean(requiresRevision);

  const blockResubmitWithoutNewFile = useMemo(
    () =>
      !requiresRevision &&
      (videoSetupData?.videoSetupVersions?.length ?? 0) > 0 &&
      !hasDirectFile &&
      !s3Uploading,
    [
      requiresRevision,
      videoSetupData?.videoSetupVersions?.length,
      hasDirectFile,
      s3Uploading,
    ],
  );

  const submitDisabled =
    !orderId ||
    !videoId ||
    isMutationLoading ||
    s3Uploading ||
    directUploadIncomplete ||
    driveNotReady ||
    isApproved ||
    (requiresApproval && !requiresRevision) ||
    blockResubmitWithoutNewFile;

  const [isOpen, setIsOpen] = useState(false);

  const Status =
    requiresRevision === true
      ? "Needs Revision"
      : requiresApproval === true
        ? "Not Approved Yet"
        : inProgress === true
          ? "In Progress"
          : isApproved === true
            ? "Approved"
            : "Optional";

  useEffect(() => {
    if (isActionRequired) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [isActionRequired]);

  const showAllVersion = !isApproved && !requiresRevision;

  return (
    <NestedAccordion
      isApproved={isApproved}
      initiallyOpen={videoSetupData?.status?.inProgress || requiresRevision}
      title="Video Setup (Optional)"
      status={Status}
      isOpen={isOpen}
      actionRequired={isActionRequired}
      setIsOpen={setIsOpen}
      timeLine={timeLine}
      initiated={videoSetupData ? true : false}
    >
      <div className="p-3">
        <BrandExtensionNotice
          requests={videoSetupData?.timelineExtensionRequests}
          stageLabel="video setup"
        />
        {!isApproved && !requiresApproval && (
          <div className="rounded-[10px] space-y-4">
            <div id="pb-direct-upload-video-setup">
              <FileUploadField
                clearFiles={clearFiles}
                onFilesUploaded={(file) => setSetupFiles(file)}
                onUploadingChange={setS3Uploading}
                maxFiles={1}
              />
            </div>

            <p className="text-center py-2">or</p>

            <div className="w-full rounded-lg border bg-gray-50 flex flex-col items-center justify-center py-6 p-4">
              <p className="text-gray-700 text-sm mb-2">
                Upload your file to Google Drive
              </p>
              <p className="text-gray-500 text-xs text-center mb-3 max-w-md">
                Open the folder, upload your file, wait until it appears in
                Drive, then submit here. Only files in this folder count (not
                subfolders).
              </p>
              <DriveFolderMobileHint variant="upload" />
              {driveLinkLoading ? (
                <p className="text-sm text-gray-600">Preparing your folder…</p>
              ) : driveLink ? (
                <DriveFolderLinkButton
                  folderUrl={driveLink}
                  className="bg-blue-500 font-medium text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
                  variant="upload"
                  onPreferDirectUpload={() =>
                    document
                      .getElementById("pb-direct-upload-video-setup")
                      ?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      })
                  }
                />
              ) : (
                <p className="text-sm text-amber-700">
                  Could not load Drive link. Refresh the page.
                </p>
              )}
            </div>
          </div>
        )}

        {isApproved && (
          <>
            <p className="text-[13px] font-medium mt-4">Final Version</p>
            <FileCard
              active={true}
              file={currentVideoSetup?.file?.[0]}
              link={currentVideoSetup?.link}
              isApproved={isApproved}
            />
          </>
        )}

        {requiresRevision && (
          <>
            <p className="text-[13px] font-medium mt-4">
              Request Revisions ({videoSetupData?.videoSetupVersions?.length})
            </p>
            <RevisionFileCard
              active={true}
              file={currentVideoSetup?.file?.[0]}
              comment={currentVideoSetup?.revisionComment as string}
              revisionImages={currentVideoSetup?.revisionImages}
            />
          </>
        )}

        <div>
          {videoSetupData?.videoSetupVersions
            ?.slice(0, showAllVersion ? undefined : -1)
            ?.reverse().length > 0 && (
            <div>
              <p className="text-[13px] font-medium mt-4">Previous Version</p>
              {videoSetupData?.videoSetupVersions
                ?.slice(0, showAllVersion ? undefined : -1)
                ?.reverse()
                .map((item, i) => (
                  <FileCard
                    key={i}
                    requiresRevision={false}
                    active={false}
                    file={item?.file?.[0]}
                    link={item?.link}
                  />
                ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-[#F5F9FF] p-3 rounded-bl-xl rounded-br-xl flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
        <p className="text-sm text-[#344054] font-medium">Cancel</p>
        <div className="flex flex-col items-end gap-1">
          {(s3Uploading || directUploadIncomplete) && (
            <p className="text-xs text-[#475467] text-right max-w-xs">
              Finishing upload to our servers—submit enables when the file is
              ready.
            </p>
          )}
          {driveNotReady && (
            <p className="text-xs text-[#475467] text-right">
              {driveLinkLoading
                ? "Preparing your Drive folder…"
                : "Open the Drive folder and upload, or wait for the link to load."}
            </p>
          )}
          <button
            type="button"
            onClick={handleUploadSetup}
            disabled={submitDisabled}
            className="text-white disabled:opacity-50 text-sm px-3 py-2 font-semibold rounded-lg bg-[#17457D]"
          >
            {isMutationLoading
              ? "Uploading…"
              : s3Uploading
                ? "Finishing upload…"
                : "Submit upload"}
          </button>
        </div>
      </div>
    </NestedAccordion>
  );
};

export default VideoSetup;
